import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Check,
  ChevronDown,
  Sparkles,
} from 'lucide-react';
import type {
  CreativePreview,
  ManualCampaignForm,
} from '@/features/campaigns/types/campaign';
import {
  AD_CATEGORIES,
  CAMPAIGN_GOALS,
} from '@/features/campaigns/constants/campaignOptions';
import { CommonSelect } from '@/features/campaigns/components/CommonSelect';
import { AgencyClientSelect } from '@/features/campaigns/components/AgencyClientSelect';
import { TargetedCities } from '@/features/campaigns/components/TargetedCities';
import { CreativeUpload } from '@/features/campaigns/components/CreativeUpload';
import { AudienceDemographic } from '@/features/campaigns/components/AudienceDemographic';
import {
  FieldError,
  campInputClass,
} from '@/features/campaigns/components/FormPrimitives';
import { sanitizeCampaignName } from '@/features/campaigns/utils/campaignUtils';
import {
  ManualDateRangePicker,
  MbBadge,
  MbHelpTooltip,
} from '@/features/campaigns/components/ManualBuilder';

type ManualGoalStepProps = {
  campaignData: ManualCampaignForm;
  setCampaignData: React.Dispatch<React.SetStateAction<ManualCampaignForm>>;
  creativePreview: CreativePreview;
  setCreativePreview: React.Dispatch<React.SetStateAction<CreativePreview>>;
  errors: Record<string, string>;
  clearError: (key: string) => void;
  allowToday?: boolean;
  /** From billboard detail — city/dates already chosen, hide Where & when. */
  hideWhereWhen?: boolean;
  onDetectingChange?: (busy: boolean) => void;
  /** Advance wizard past step 1 (audience is optional). */
  onContinueToNextStep?: () => void;
  continueLabel?: string;
};

export function ManualGoalStep({
  campaignData,
  setCampaignData,
  creativePreview,
  setCreativePreview,
  errors,
  clearError,
  allowToday = false,
  hideWhereWhen = false,
  onDetectingChange,
  onContinueToNextStep,
  continueLabel = 'Continue to Select Screens',
}: ManualGoalStepProps) {
  // Section refs for auto-scroll
  const basicsRef = useRef<HTMLDivElement>(null);
  const scheduleRef = useRef<HTMLDivElement>(null);
  const creativeRef = useRef<HTMLDivElement>(null);
  const audienceRef = useRef<HTMLDivElement>(null);

  // Accordion state
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    basics: true,
    schedule: false,
    creative: false,
    audience: false,
  });

  const scrollToRef = (targetRef: React.RefObject<HTMLDivElement | null>) => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        targetRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 120);
    });
  };

  const toggleSection = (
    section: string,
    targetRef?: React.RefObject<HTMLDivElement | null>,
  ) => {
    setOpenSections((prev) => {
      const willOpen = !prev[section];
      if (willOpen && targetRef) {
        scrollToRef(targetRef);
      }
      return {
        ...prev,
        [section]: willOpen,
      };
    });
  };

  const advanceTo = (
    currentSection: string,
    nextSection: string,
    nextRef: React.RefObject<HTMLDivElement | null>,
  ) => {
    setOpenSections((prev) => ({
      ...prev,
      [currentSection]: false,
      [nextSection]: true,
    }));
    scrollToRef(nextRef);
  };

  const handleFieldFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLElement>,
  ) => {
    e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // Auto-expand & scroll to the first section with an error when validation occurs
  useEffect(() => {
    if (
      errors.campaignName ||
      errors.campaignGoals ||
      errors.adType ||
      errors.agencyClientId
    ) {
      setOpenSections((prev) => ({ ...prev, basics: true }));
      scrollToRef(basicsRef);
    } else if (
      !hideWhereWhen &&
      (errors.targetCities || errors.startDate || errors.endDate)
    ) {
      setOpenSections((prev) => ({ ...prev, schedule: true }));
      scrollToRef(scheduleRef);
    } else if (errors.mediaId || errors.customDuration) {
      setOpenSections((prev) => ({ ...prev, creative: true }));
      scrollToRef(creativeRef);
    }
  }, [errors, hideWhereWhen]);

  // Section completion checks
  const isBasicsComplete = Boolean(
    campaignData.campaignName.trim() &&
      campaignData.campaignGoals &&
      campaignData.adType,
  );
  const isScheduleComplete = Boolean(
    campaignData.targetCities.length > 0 &&
      campaignData.startDate &&
      campaignData.endDate,
  );
  const isCreativeComplete = Boolean(campaignData.mediaIds?.[0]?.mediaId);
  const isAudienceComplete = Boolean(
    campaignData.economicActivity || campaignData.lifestyle,
  );

  const rawOrientation = String(
    campaignData.mediaIds?.[0]?.orientation || '',
  ).toUpperCase();
  const hasValidOrientation =
    rawOrientation === 'LANDSCAPE' ||
    rawOrientation === 'PORTRAIT' ||
    rawOrientation === 'SQUARE';

  return (
    <div className="flex w-full flex-col gap-3.5">
      <header className="flex items-end justify-between gap-3 text-left">
        <div className="min-w-0">
          <h1 className="m-0 text-xl font-semibold tracking-tight text-[var(--dash-ink)]">
            Campaign details
          </h1>
          <p className="mt-0.5 mb-0 text-[13px] text-[var(--dash-muted)]">
            {hideWhereWhen
              ? 'Name, creative, and settings — screen already selected.'
              : 'Name, cities, dates, and creative configuration.'}
          </p>
        </div>
        {hasValidOrientation ? (
          <MbBadge tone="brand">{rawOrientation} creative</MbBadge>
        ) : (
          <MbBadge>Step 1</MbBadge>
        )}
      </header>

      {/* Accordion 1: Campaign Basics */}
      <div
        ref={basicsRef}
        className={`scroll-mt-4 overflow-hidden rounded-2xl border transition-all duration-300 ${
          errors.campaignName || errors.campaignGoals || errors.adType
            ? 'border-red-300 bg-white shadow-xs'
            : openSections.basics
              ? 'border-[var(--dash-brand)]/40 bg-white shadow-[0_4px_24px_-8px_rgba(41,121,255,0.12)] ring-1 ring-[var(--dash-brand)]/15'
              : 'border-[#E5EAF0] bg-white hover:border-gray-300'
        }`}
      >
        <button
          type="button"
          onClick={() => toggleSection('basics', basicsRef)}
          className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-[#F9FBFC]"
          aria-expanded={openSections.basics}
        >
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                errors.campaignName || errors.campaignGoals || errors.adType
                  ? 'bg-red-100 text-red-600'
                  : isBasicsComplete
                    ? 'bg-emerald-500 text-white shadow-xs'
                    : openSections.basics
                      ? 'bg-[var(--dash-brand)] text-white'
                      : 'bg-gray-100 text-gray-500'
              }`}
            >
              {isBasicsComplete ? <Check className="h-4 w-4 stroke-[2.5]" /> : '1'}
            </span>
            <div className="min-w-0">
              <h2 className="m-0 text-sm font-semibold tracking-tight text-[var(--dash-ink)]">
                Campaign Basics
              </h2>
              <p className="m-0 truncate text-[12px] text-[var(--dash-muted)]">
                {isBasicsComplete && !openSections.basics
                  ? `${campaignData.campaignName} · ${campaignData.campaignGoals} · ${campaignData.adType}`
                  : 'Campaign name, objective and ad category'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isBasicsComplete && !openSections.basics ? (
              <span className="hidden items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 sm:inline-flex">
                <Check className="h-3 w-3 stroke-[2.5]" /> Done
              </span>
            ) : null}
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${
                openSections.basics ? 'rotate-180 text-[var(--dash-brand)]' : ''
              }`}
            />
          </div>
        </button>

        <div
          className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
            openSections.basics
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0 pointer-events-none'
          }`}
        >
          <div className="overflow-hidden">
            <div className="border-t border-[var(--dash-line)] p-4 sm:p-5">
              <div className="mb-fields">
                <AgencyClientSelect
                  value={campaignData.agencyClientId}
                  error={errors.agencyClientId}
                  onChange={(clientId) => {
                    clearError('agencyClientId');
                    setCampaignData((prev) => ({
                      ...prev,
                      agencyClientId: clientId,
                    }));
                  }}
                />

                <div className="mb-field">
                  <div className="mb-field__label-row">
                    <span className="mb-field-label">
                      Campaign name <span className="text-red-500">*</span>
                    </span>
                    <MbHelpTooltip label="About campaign name">
                      Shown on your campaign list and reports. Keep it short and
                      recognizable for your team.
                    </MbHelpTooltip>
                  </div>
                  <input
                    value={campaignData.campaignName}
                    maxLength={50}
                    onFocus={handleFieldFocus}
                    onChange={(e) => {
                      clearError('campaignName');
                      setCampaignData((prev) => ({
                        ...prev,
                        campaignName: sanitizeCampaignName(e.target.value),
                      }));
                    }}
                    placeholder="e.g. Summer Launch Blitz"
                    aria-invalid={Boolean(errors.campaignName)}
                    className={`${campInputClass} ${errors.campaignName ? 'is-invalid' : ''}`}
                  />
                  <div className="flex min-h-[1.1rem] items-center justify-between">
                    <FieldError message={errors.campaignName} />
                    <span className="ml-auto text-[11px] text-[var(--dash-muted)]">
                      {campaignData.campaignName.length}/50
                    </span>
                  </div>
                </div>

                <div className="mb-field-grid">
                  <CommonSelect
                    label="Campaign objective"
                    name="campaignGoals"
                    value={campaignData.campaignGoals}
                    options={[...CAMPAIGN_GOALS]}
                    error={errors.campaignGoals}
                    required
                    searchable
                    compact
                    onChange={(name, value) => {
                      clearError(name);
                      setCampaignData((prev) => ({
                        ...prev,
                        campaignGoals: value as ManualCampaignForm['campaignGoals'],
                      }));
                    }}
                  />
                  <CommonSelect
                    label="Ad category"
                    name="adType"
                    value={campaignData.adType}
                    options={Object.entries(AD_CATEGORIES).map(([key, label]) => ({
                      key,
                      label,
                    }))}
                    error={errors.adType}
                    required
                    searchable
                    compact
                    onChange={(name, value) => {
                      clearError(name);
                      setCampaignData((prev) => ({ ...prev, adType: value }));
                    }}
                  />
                </div>

                <div className="mt-2 flex justify-end border-t border-[var(--dash-line)] pt-3">
                  <button
                    type="button"
                    onClick={() =>
                      advanceTo(
                        'basics',
                        hideWhereWhen ? 'creative' : 'schedule',
                        hideWhereWhen ? creativeRef : scheduleRef,
                      )
                    }
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--dash-brand)] px-4 py-2 text-xs font-semibold text-white shadow-xs transition hover:opacity-95"
                  >
                    Continue to {hideWhereWhen ? 'Creative' : 'Schedule'}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Accordion 2: Where & When (Targeting & Flight Dates) */}
      {!hideWhereWhen ? (
        <div
          ref={scheduleRef}
          className={`scroll-mt-4 overflow-hidden rounded-[18px] border transition-all duration-300 ${
            errors.targetCities || errors.startDate || errors.endDate
              ? 'border-red-300 bg-white shadow-xs'
              : openSections.schedule
                ? 'border-black/[0.08] bg-white shadow-[0_8px_30px_-12px_rgba(16,38,73,0.18)]'
                : 'border-black/[0.06] bg-white/90 hover:border-black/[0.12] hover:shadow-[0_2px_12px_-4px_rgba(16,38,73,0.08)]'
          }`}
        >
          <button
            type="button"
            onClick={() => toggleSection('schedule', scheduleRef)}
            className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-[#F8FAFC]/80"
            aria-expanded={openSections.schedule}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tracking-tight transition-all duration-300 ${
                  errors.targetCities || errors.startDate || errors.endDate
                    ? 'bg-red-100 text-red-600'
                    : isScheduleComplete
                      ? 'bg-emerald-500 text-white shadow-[0_2px_8px_-2px_rgba(16,185,129,0.55)]'
                      : openSections.schedule
                        ? 'bg-[var(--dash-brand)] text-white shadow-[0_2px_8px_-2px_rgba(41,121,255,0.55)]'
                        : 'bg-[#EEF1F5] text-[#6B7789]'
                }`}
              >
                {isScheduleComplete ? (
                  <Check className="h-3.5 w-3.5 stroke-[2.5]" />
                ) : (
                  '2'
                )}
              </span>
              <div className="min-w-0">
                <h2 className="m-0 text-[14px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]">
                  Schedule & Target Cities
                </h2>
                <p className="m-0 truncate text-[12px] tracking-[-0.01em] text-[var(--dash-muted)]">
                  {isScheduleComplete && !openSections.schedule
                    ? `${campaignData.targetCities.join(', ')} · ${campaignData.startDate} → ${campaignData.endDate}`
                    : 'Markets and campaign flight window'}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {isScheduleComplete && !openSections.schedule ? (
                <span className="hidden items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100 sm:inline-flex">
                  <Check className="h-3 w-3 stroke-[2.5]" /> Done
                </span>
              ) : null}
              <ChevronDown
                className={`h-4 w-4 text-[#9AA8B8] transition-transform duration-300 ${
                  openSections.schedule
                    ? 'rotate-180 text-[var(--dash-brand)]'
                    : ''
                }`}
              />
            </div>
          </button>

          <div
            className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
              openSections.schedule
                ? 'grid-rows-[1fr] opacity-100'
                : 'grid-rows-[0fr] opacity-0 pointer-events-none'
            }`}
          >
            <div className="overflow-hidden">
              <div className="border-t border-black/[0.06] bg-[#F4F6F9]/60 p-4 sm:p-5">
                <p className="mb-schedule-hint">
                  <strong>Where & when.</strong> Pick markets first, then set the
                  flight window — inventory suggestions follow these choices.
                </p>

                <div className="mb-schedule-group">
                  <div className="mb-schedule-group__row">
                    <TargetedCities
                      selectedCities={campaignData.targetCities}
                      error={errors.targetCities}
                      onChange={(cities) => {
                        clearError('targetCities');
                        setCampaignData((prev) => ({
                          ...prev,
                          targetCities: cities,
                        }));
                      }}
                    />
                  </div>
                  <div className="mb-schedule-group__row">
                    <ManualDateRangePicker
                      startDate={campaignData.startDate}
                      endDate={campaignData.endDate}
                      allowToday={allowToday}
                      errors={errors}
                      clearError={clearError}
                      onChange={({ startDate, endDate }) => {
                        setCampaignData((prev) => ({
                          ...prev,
                          startDate,
                          endDate,
                        }));
                      }}
                    />
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3">
                  <p className="m-0 hidden text-[11px] text-[var(--dash-muted)] sm:block">
                    {isScheduleComplete
                      ? 'Ready — continue to upload creative'
                      : 'Cities and dates required to continue'}
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      advanceTo('schedule', 'creative', creativeRef)
                    }
                    className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[var(--dash-brand)] px-4 py-2 text-[12px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(41,121,255,0.55)] transition hover:opacity-95 active:scale-[0.98]"
                  >
                    Continue to Creative
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Accordion 3: Creative & Display Setup */}
      <div
        ref={creativeRef}
        className={`scroll-mt-4 overflow-hidden rounded-[18px] border transition-all duration-300 ${
          errors.mediaId || errors.customDuration
            ? 'border-red-300 bg-white shadow-xs'
            : openSections.creative
              ? 'border-black/[0.08] bg-white shadow-[0_8px_30px_-12px_rgba(16,38,73,0.18)]'
              : 'border-black/[0.06] bg-white/90 hover:border-black/[0.12] hover:shadow-[0_2px_12px_-4px_rgba(16,38,73,0.08)]'
        }`}
      >
        <button
          type="button"
          onClick={() => toggleSection('creative', creativeRef)}
          className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-[#F8FAFC]/80"
          aria-expanded={openSections.creative}
        >
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tracking-tight transition-all duration-300 ${
                errors.mediaId || errors.customDuration
                  ? 'bg-red-100 text-red-600'
                  : isCreativeComplete
                    ? 'bg-emerald-500 text-white shadow-[0_2px_8px_-2px_rgba(16,185,129,0.55)]'
                    : openSections.creative
                      ? 'bg-[var(--dash-brand)] text-white shadow-[0_2px_8px_-2px_rgba(41,121,255,0.55)]'
                      : 'bg-[#EEF1F5] text-[#6B7789]'
              }`}
            >
              {isCreativeComplete ? (
                <Check className="h-3.5 w-3.5 stroke-[2.5]" />
              ) : hideWhereWhen ? (
                '2'
              ) : (
                '3'
              )}
            </span>
            <div className="min-w-0">
              <h2 className="m-0 text-[14px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]">
                Creative & Ad Duration
              </h2>
              <p className="m-0 truncate text-[12px] tracking-[-0.01em] text-[var(--dash-muted)]">
                {isCreativeComplete && !openSections.creative
                  ? `${rawOrientation || 'Active'} · ${campaignData.customDuration || '30'}s spot`
                  : 'Media upload and spot length'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isCreativeComplete && !openSections.creative ? (
              <span className="hidden items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100 sm:inline-flex">
                <Check className="h-3 w-3 stroke-[2.5]" /> Done
              </span>
            ) : null}
            <ChevronDown
              className={`h-4 w-4 text-[#9AA8B8] transition-transform duration-300 ${
                openSections.creative ? 'rotate-180 text-[var(--dash-brand)]' : ''
              }`}
            />
          </div>
        </button>

        <div
          className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
            openSections.creative
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0 pointer-events-none'
          }`}
        >
          <div className="overflow-hidden">
            <div className="border-t border-black/[0.06] bg-[#F4F6F9]/60 p-4 sm:p-5">
              <div className="mb-schedule-hint flex items-start gap-2">
                <Sparkles
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--dash-brand)]"
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <span>
                    <strong>Auto-detected orientation.</strong> Use Live Preview
                    tabs (Landscape / Portrait) — mismatched screens stretch
                    creatives to fill.
                  </span>
                </div>
                <MbHelpTooltip label="About orientation detection">
                  Orientation comes from media dimensions via creative
                  detection. Matching screens look best; cross-orientation
                  playback stretches/crops to fill.
                </MbHelpTooltip>
              </div>

              <div className="mb-schedule-group">
                <div className="mb-schedule-group__row">
                  <CreativeUpload
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    creativePreview={creativePreview}
                    setCreativePreview={setCreativePreview}
                    error={errors.mediaId || errors.customDuration}
                    clearError={clearError}
                    hidePreviewMedia={true}
                    onBusyChange={onDetectingChange}
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() =>
                    advanceTo('creative', 'audience', audienceRef)
                  }
                  className="order-2 text-left text-[11px] font-semibold text-[var(--dash-muted)] transition hover:text-[var(--dash-brand)] sm:order-1"
                >
                  {isAudienceComplete
                    ? 'Edit optional audience targeting'
                    : 'Add optional audience targeting'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (onContinueToNextStep) {
                      onContinueToNextStep();
                      return;
                    }
                    advanceTo('creative', 'audience', audienceRef);
                  }}
                  className="order-1 ml-auto inline-flex items-center gap-1.5 rounded-full bg-[var(--dash-brand)] px-4 py-2 text-[12px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(41,121,255,0.55)] transition hover:opacity-95 active:scale-[0.98] sm:order-2"
                >
                  {continueLabel}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Accordion 4: Audience Demographics (Optional) */}
      <div
        ref={audienceRef}
        className={`scroll-mt-4 overflow-hidden rounded-[18px] border transition-all duration-300 ${
          openSections.audience
            ? 'border-black/[0.08] bg-white shadow-[0_8px_30px_-12px_rgba(16,38,73,0.18)]'
            : 'border-black/[0.06] bg-white/90 hover:border-black/[0.12] hover:shadow-[0_2px_12px_-4px_rgba(16,38,73,0.08)]'
        }`}
      >
        <button
          type="button"
          onClick={() => toggleSection('audience', audienceRef)}
          className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-[#F8FAFC]/80"
          aria-expanded={openSections.audience}
        >
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tracking-tight transition-all duration-300 ${
                isAudienceComplete
                  ? 'bg-emerald-500 text-white shadow-[0_2px_8px_-2px_rgba(16,185,129,0.55)]'
                  : openSections.audience
                    ? 'bg-[var(--dash-brand)] text-white shadow-[0_2px_8px_-2px_rgba(41,121,255,0.55)]'
                    : 'bg-[#EEF1F5] text-[#6B7789]'
              }`}
            >
              {isAudienceComplete ? (
                <Check className="h-3.5 w-3.5 stroke-[2.5]" />
              ) : hideWhereWhen ? (
                '3'
              ) : (
                '4'
              )}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="m-0 text-[14px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]">
                  Audience Targeting
                </h2>
                <MbBadge>Optional</MbBadge>
              </div>
              <p className="m-0 truncate text-[12px] tracking-[-0.01em] text-[var(--dash-muted)]">
                {isAudienceComplete && !openSections.audience
                  ? `${campaignData.economicActivity || '—'} · ${campaignData.lifestyle || '—'} · ${campaignData.buyingPowerZone || 'Buying power'}`
                  : 'Economic zone & lifestyle — skip anytime'}
              </p>
            </div>
          </div>
          <ChevronDown
            className={`h-4 w-4 text-[#9AA8B8] transition-transform duration-300 ${
              openSections.audience
                ? 'rotate-180 text-[var(--dash-brand)]'
                : ''
            }`}
          />
        </button>

        <div
          className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
            openSections.audience
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0 pointer-events-none'
          }`}
        >
          <div className="overflow-hidden">
            <div className="border-t border-black/[0.06] bg-[#F4F6F9]/60 p-4 sm:p-5">
              <p className="mb-schedule-hint">
                <strong>Optional fine-tuning.</strong> These segments help
                estimate buying power — they are not required to book screens.
              </p>

              <div className="mb-schedule-group">
                <div className="mb-schedule-group__row">
                  <AudienceDemographic
                    formData={campaignData}
                    onChange={(patch) =>
                      setCampaignData((prev) => ({ ...prev, ...patch }))
                    }
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() =>
                    setOpenSections((prev) => ({ ...prev, audience: false }))
                  }
                  className="order-2 text-left text-[11px] font-semibold text-[var(--dash-muted)] transition hover:text-[var(--dash-ink)] sm:order-1"
                >
                  Skip audience for now
                </button>
                {onContinueToNextStep ? (
                  <button
                    type="button"
                    onClick={onContinueToNextStep}
                    className="order-1 ml-auto inline-flex items-center gap-1.5 rounded-full bg-[var(--dash-brand)] px-4 py-2 text-[12px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(41,121,255,0.55)] transition hover:opacity-95 active:scale-[0.98] sm:order-2"
                  >
                    {continueLabel}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
